/**
 * Cookie Consent Banner
 *
 * LGPD-compliant cookie consent component that:
 * - Shows a fixed banner at the bottom of the screen on public/landing pages only
 * - Blocks all marketing pixels (GTM, Meta Pixel, Clarity) from loading until consent
 * - Dynamically injects scripts when user accepts all cookies
 * - Saves preference to localStorage
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, alpha } from '@mui/material';
import CookieIcon from '@mui/icons-material/Cookie';
import { useLocation } from 'react-router-dom';
import { landingColors } from '../../theme/landingTheme';
import { env } from '../../config/environment';

const CONSENT_KEY = 'clinafy_cookie_consent';

interface CookieConsentPreference {
  accepted: boolean;
  essential_only: boolean;
  timestamp: string;
}

/** Routes that are considered "app" (not public). Banner won't show here. */
const APP_ROUTE_PREFIXES = [
  '/dashboard',
  '/admin',
  '/patients',
  '/sessions',
  '/calendar',
  '/team',
  '/settings',
  '/roles',
  '/subscription',
  '/profile',
  '/notifications',
  '/branding',
  '/referrals',
  '/upgrade',
  '/waiting-list',
  '/documents',
  '/templates',
  '/online-booking',
  '/reports',
  '/insurance',
  '/export',
  '/video-call',
  '/portal',
];

function getStoredConsent(): CookieConsentPreference | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsentPreference;
  } catch {
    return null;
  }
}

function saveConsent(preference: CookieConsentPreference): void {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(preference));
  } catch {
    // Silently fail if localStorage is full
  }
}

function injectGTM(gtmId: string): void {
  if (!gtmId || document.getElementById('gtm-script')) return;

  // Inject GTM script into <head>
  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');`;
  document.head.appendChild(script);

  // Inject noscript iframe into <body>
  const noscript = document.createElement('noscript');
  noscript.id = 'gtm-noscript';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function injectMetaPixel(pixelId: string): void {
  if (!pixelId || document.getElementById('meta-pixel-script')) return;

  const script = document.createElement('script');
  script.id = 'meta-pixel-script';
  script.innerHTML = `!function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');`;
  document.head.appendChild(script);

  // noscript fallback for browsers without JS
  const noscript = document.createElement('noscript');
  noscript.id = 'meta-pixel-noscript';
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function injectClarity(projectId: string): void {
  if (!projectId || document.getElementById('clarity-script')) return;

  const script = document.createElement('script');
  script.id = 'clarity-script';
  script.innerHTML = `(function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");`;
  document.head.appendChild(script);
}

function injectAllPixels(): void {
  injectGTM(env.tracking.gtmId);
  injectMetaPixel(env.tracking.facebookPixelId);
  injectClarity(env.tracking.clarityProjectId);
}

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  // Determine if current route is a public page
  const isPublicPage = !APP_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  useEffect(() => {
    const consent = getStoredConsent();
    if (consent) {
      // Already consented - inject all marketing pixels if they accepted all
      if (consent.accepted && !consent.essential_only) {
        injectAllPixels();
      }
      setVisible(false);
    } else {
      // No consent yet - show banner on public pages
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const preference: CookieConsentPreference = {
      accepted: true,
      essential_only: false,
      timestamp: new Date().toISOString(),
    };
    saveConsent(preference);
    injectAllPixels();
    setVisible(false);
  }, []);

  const handleEssentialOnly = useCallback(() => {
    const preference: CookieConsentPreference = {
      accepted: true,
      essential_only: true,
      timestamp: new Date().toISOString(),
    };
    saveConsent(preference);
    setVisible(false);
  }, []);

  // Don't render on app pages or if consent already given
  if (!visible || !isPublicPage) return null;

  return (
    <Box
      role="dialog"
      aria-label="Consentimento de cookies"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        bgcolor: alpha(landingColors.neutral.gray900, 0.95),
        backdropFilter: 'blur(8px)',
        borderTop: `1px solid ${alpha(landingColors.neutral.white, 0.1)}`,
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 2.5 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
          <CookieIcon
            sx={{
              color: landingColors.accent.yellow,
              fontSize: 24,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              color: landingColors.neutral.white,
              fontSize: { xs: '0.85rem', md: '0.9rem' },
              lineHeight: 1.6,
            }}
          >
            Utilizamos cookies para melhorar sua experiência, analisar o tráfego do site e
            personalizar conteúdo. Ao clicar em "Aceitar todos", você concorda com o uso de
            cookies analíticos e de marketing. Saiba mais em nossa política de privacidade.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Button
            variant="outlined"
            onClick={handleEssentialOnly}
            sx={{
              color: landingColors.neutral.white,
              borderColor: alpha(landingColors.neutral.white, 0.4),
              fontWeight: 500,
              fontSize: '0.85rem',
              px: 2.5,
              py: 1,
              borderRadius: 2,
              whiteSpace: 'nowrap',
              flex: { xs: 1, md: 'none' },
              '&:hover': {
                borderColor: landingColors.neutral.white,
                bgcolor: alpha(landingColors.neutral.white, 0.08),
              },
            }}
          >
            Apenas essenciais
          </Button>
          <Button
            variant="contained"
            onClick={handleAcceptAll}
            sx={{
              bgcolor: landingColors.accent.teal,
              color: landingColors.neutral.white,
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2.5,
              py: 1,
              borderRadius: 2,
              whiteSpace: 'nowrap',
              flex: { xs: 1, md: 'none' },
              '&:hover': {
                bgcolor: landingColors.primary.main,
              },
            }}
          >
            Aceitar todos
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CookieConsent;
